/**
 * Stable machine-readable API error codes.
 *
 * Source of truth: `docs/design/backend-contracts-v0.1.md` §3.4 (error code
 * families) plus the codes named explicitly elsewhere in that document
 * (§3.3 `ATTEMPT_ALREADY_SUBMITTED`, §4.6 `ASSESSMENT_NOT_AVAILABLE`).
 *
 * Contract rules carried over from the design:
 *   - Codes are stable; the human-readable `message` is not part of the contract.
 *   - Errors must never disclose whether another family's resource exists
 *     (authorization failures collapse to a generic unavailable state).
 *   - No SQL text, stack traces, provider payloads or learner content.
 *
 * Phase 1 defines the vocabulary only. The endpoints that raise these codes
 * are implemented in later phases.
 */

export const ERROR_FAMILY = {
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  VALIDATION: 'validation',
  STATE_CONFLICT: 'state_conflict',
  LIMITS: 'limits',
  PROVIDER: 'provider',
  INTERNAL: 'internal',
} as const

export type ErrorFamily = (typeof ERROR_FAMILY)[keyof typeof ERROR_FAMILY]

export const ERROR_CODE = {
  /* Authentication — client should re-authenticate. */
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  /** The learner session (D-16) ended: the learner enters their PIN again. */
  LEARNER_SESSION_INVALID: 'LEARNER_SESSION_INVALID',

  /* Authorization — client shows a generic unavailable state. */
  ACCESS_DENIED: 'ACCESS_DENIED',
  RESOURCE_NOT_AVAILABLE: 'RESOURCE_NOT_AVAILABLE',

  /* Validation — client shows a field or task level error. */
  INVALID_INPUT: 'INVALID_INPUT',
  UNSUPPORTED_SCHEMA_VERSION: 'UNSUPPORTED_SCHEMA_VERSION',

  /* State conflict — client refreshes state; must not blindly retry. */
  INVALID_STATE: 'INVALID_STATE',
  ALREADY_SUBMITTED: 'ALREADY_SUBMITTED',
  ATTEMPT_ALREADY_SUBMITTED: 'ATTEMPT_ALREADY_SUBMITTED',
  IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',

  /* Limits — learning data is always preserved; only AI work is deferred. */
  AI_BUDGET_EXCEEDED: 'AI_BUDGET_EXCEEDED',
  DAILY_ASSESSMENT_LIMIT: 'DAILY_ASSESSMENT_LIMIT',
  RATE_LIMITED: 'RATE_LIMITED',
  ASSESSMENT_NOT_AVAILABLE: 'ASSESSMENT_NOT_AVAILABLE',

  /* Provider — bounded retry queue. */
  PROVIDER_TEMPORARY_FAILURE: 'PROVIDER_TEMPORARY_FAILURE',
  PROVIDER_TIMEOUT: 'PROVIDER_TIMEOUT',

  /* Internal — safe message plus request id. */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const

export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE]

const FAMILY_BY_CODE: Readonly<Record<ErrorCode, ErrorFamily>> = {
  UNAUTHENTICATED: ERROR_FAMILY.AUTHENTICATION,
  SESSION_EXPIRED: ERROR_FAMILY.AUTHENTICATION,
  LEARNER_SESSION_INVALID: ERROR_FAMILY.AUTHENTICATION,

  ACCESS_DENIED: ERROR_FAMILY.AUTHORIZATION,
  RESOURCE_NOT_AVAILABLE: ERROR_FAMILY.AUTHORIZATION,

  INVALID_INPUT: ERROR_FAMILY.VALIDATION,
  UNSUPPORTED_SCHEMA_VERSION: ERROR_FAMILY.VALIDATION,

  INVALID_STATE: ERROR_FAMILY.STATE_CONFLICT,
  ALREADY_SUBMITTED: ERROR_FAMILY.STATE_CONFLICT,
  ATTEMPT_ALREADY_SUBMITTED: ERROR_FAMILY.STATE_CONFLICT,
  IDEMPOTENCY_CONFLICT: ERROR_FAMILY.STATE_CONFLICT,

  AI_BUDGET_EXCEEDED: ERROR_FAMILY.LIMITS,
  DAILY_ASSESSMENT_LIMIT: ERROR_FAMILY.LIMITS,
  RATE_LIMITED: ERROR_FAMILY.LIMITS,
  ASSESSMENT_NOT_AVAILABLE: ERROR_FAMILY.LIMITS,

  PROVIDER_TEMPORARY_FAILURE: ERROR_FAMILY.PROVIDER,
  PROVIDER_TIMEOUT: ERROR_FAMILY.PROVIDER,

  INTERNAL_ERROR: ERROR_FAMILY.INTERNAL,
}

/**
 * Codes whose default `retryable` flag is `true`.
 *
 * A server response may override this: the wire `retryable` field wins. This
 * table is the client-side default used when constructing an error locally or
 * when the server omits the flag.
 */
const RETRYABLE_BY_DEFAULT: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  ERROR_CODE.PROVIDER_TEMPORARY_FAILURE,
  ERROR_CODE.PROVIDER_TIMEOUT,
  ERROR_CODE.RATE_LIMITED,
])

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(FAMILY_BY_CODE, value)
}

export function errorFamily(code: ErrorCode): ErrorFamily {
  return FAMILY_BY_CODE[code]
}

export function isRetryableByDefault(code: ErrorCode): boolean {
  return RETRYABLE_BY_DEFAULT.has(code)
}

/**
 * True when the client must send the user back through authentication.
 */
export function requiresReauthentication(code: ErrorCode): boolean {
  return errorFamily(code) === ERROR_FAMILY.AUTHENTICATION
}

/**
 * True when the failure must not be retried with the same payload.
 * State conflicts require the client to refresh server state first.
 */
export function requiresStateRefresh(code: ErrorCode): boolean {
  return errorFamily(code) === ERROR_FAMILY.STATE_CONFLICT
}
